// Правая колонка представления «Предложения». Показывает одно из двух — и это не два экрана,
// а два уровня одной работы.
//
// Выбрано предложение — карточка предложения: исходное сообщение, что из него достала модель,
// какое решение принято. Предложение не выбрано, но отбор стоит по выгрузке — карточка самой
// выгрузки: сколько разобрано, сколько ждёт решения, протокол разбора и кнопка «разобрать».
// Второе не заглушка «выберите строку»: разбор выгрузки — настоящее действие над настоящим
// объектом, и жить оно обязано там, где этот объект (правило 3).
//
// ## Протокол разбора показывается КАК ЕСТЬ
//
// Это перечень того, что модель предложила, а проверка отбросила: имя не нашлось в исходном
// тексте, телефон не сошёлся, кусок не разобрался вовсе. Причёсывать его нельзя — по нему
// видно, чего в предложениях НЕ хватает, а честный след важнее аккуратного вида.

import { useState } from 'react';

import {
  RiErrorWarningLine,
  RiInboxUnarchiveLine,
  RiRobot2Line,
  RiScales3Line,
  RiSparkling2Line,
} from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import { formatDateDMY, formatDateOnly, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import type { BlacklistDraftOut, MessengerImportOut } from '../api/blacklist';
import { GateNote, useCan } from '../components/gates';
import {
  DRAFT_VERDICT_COLORS,
  IMPORT_STATUS_TITLES,
  MESSENGER_GROUP_TITLES,
} from '../dictionaries';
import { useDraftsQuery, useImportsQuery, useParseImportMutation } from '../queries';
import type { HiringRoute } from '../route';
import { FILTER_IMPORT, PANEL_DRAFT_DECISION, hiringHref, openPanelHref } from '../route';

import { draftsQueryFromRoute } from '../views/DraftsList';

/** Разбор выгрузки и решение по предложению: право рекрутёра и администратора. */
const EDIT_PERMISSION = 'hiring.blacklist.edit';

export function DraftDetail({ route }: { route: HiringRoute }) {
  const выгрузкаId = route.search.filters[FILTER_IMPORT];

  if (route.rowId !== undefined) {
    return <КарточкаПредложения draftId={route.rowId} route={route} />;
  }
  if (выгрузкаId !== undefined) {
    return <КарточкаВыгрузки importId={выгрузкаId} />;
  }
  return (
    <DetailPane>
      <div className='flex h-full items-center justify-center'>
        <EmptyState
          icon={RiSparkling2Line}
          title='Предложение не выбрано'
          description='Выберите предложение в списке слева — здесь откроется исходное сообщение, то, что из него достала модель, и решение по нему. Выберите выгрузку в отборе — откроется её карточка с разбором.'
        />
      </div>
    </DetailPane>
  );
}

// ── Карточка выгрузки ───────────────────────────────────────────────────────────────────

function КарточкаВыгрузки({ importId }: { importId: string }) {
  const imports = useImportsQuery();
  const выгрузка = imports.data?.items.find((строка) => строка.id === importId);

  return (
    <DetailPane
      title={
        <span className='flex min-w-0 items-center gap-2'>
          <RiInboxUnarchiveLine className='size-5 shrink-0 text-text-soft-400' />
          <span className='truncate'>
            {выгрузка === undefined
              ? 'Выгрузка мессенджера'
              : `Выгрузка: ${MESSENGER_GROUP_TITLES[выгрузка.group_kind] ?? выгрузка.group_kind}`}
          </span>
          {выгрузка !== undefined && (
            <Badge.Root variant='lighter' color='gray' size='medium'>
              {IMPORT_STATUS_TITLES[выгрузка.status] ?? выгрузка.status}
            </Badge.Root>
          )}
        </span>
      }
    >
      <ScreenState
        data={imports.data}
        error={imports.error}
        subject='выгрузки мессенджера'
        // Выгрузка, которой нет в списке, — это пустое состояние, а не ошибка: ссылку могли
        // прислать на уже уничтоженную выгрузку, и «не удалось загрузить» об этом соврало бы.
        isEmpty={() => выгрузка === undefined}
        onRetry={() => void imports.refetch()}
        loading={
          <div className='flex flex-col gap-3 p-5'>
            <Skeleton className='h-24 w-full' />
            <Skeleton className='h-24 w-full' />
          </div>
        }
        empty={
          <div className='flex h-full items-center justify-center'>
            <EmptyState
              icon={RiInboxUnarchiveLine}
              title='Этой выгрузки больше нет'
              description='Она либо уничтожена по сроку хранения, либо ссылка устарела. Предложения, по которым уже приняли решения, при этом остались на месте.'
            />
          </div>
        }
      >
        {() => (выгрузка === undefined ? null : <ТелоВыгрузки выгрузка={выгрузка} />)}
      </ScreenState>
    </DetailPane>
  );
}

function ТелоВыгрузки({ выгрузка }: { выгрузка: MessengerImportOut }) {
  const allowed = useCan(EDIT_PERMISSION);
  const mutation = useParseImportMutation();
  const [gateNote, setGateNote] = useState<string | null>(null);

  const принята = parseMoment(выгрузка.created_at);
  const можноРазбирать = выгрузка.status === 'uploaded';

  function разобрать() {
    if (!allowed) {
      setGateNote(
        'Разбирать выгрузки может рекрутёр или администратор. Попросите открыть это право.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Разбор уже идёт, ждём ответ сервера. Второй раз не запускаем.');
      return;
    }
    if (выгрузка.file_id === null || выгрузка.file_id === undefined) {
      // Текст выгрузки, вставленный при приёме, нигде не хранится: он уходит прямо в разбор.
      // Значит повторить разбор отсюда нечем, и честнее сказать это, чем отправить запрос,
      // который вернётся отказом «разбирать нечего».
      setGateNote(
        'Текст этой выгрузки в системе не сохранён: он передавался прямо при разборе. Чтобы разобрать её заново, примите выгрузку ещё раз вместе с текстом.',
      );
      return;
    }
    setGateNote(null);
    mutation.mutate({ importId: выгрузка.id, text: null });
  }

  return (
    <div className='flex flex-col gap-3 p-5'>
      <p className='text-paragraph-sm text-text-sub-600'>
        Разбор даёт только предложения. В чёрный список отсюда не попадает никто: по каждой строке
        решение принимает человек.
      </p>

      <FieldList title='Что в выгрузке'>
        <FieldRow
          label='Группа мессенджера'
          value={MESSENGER_GROUP_TITLES[выгрузка.group_kind] ?? выгрузка.group_kind}
        />
        <FieldRow
          label='Состояние'
          value={IMPORT_STATUS_TITLES[выгрузка.status] ?? выгрузка.status}
          // Неудавшийся разбор — тупик, и сказать об этом надо здесь, а не оставлять человека
          // искать кнопку, которой нет: повторно разобрать выгрузку сервер не даёт, состояние
          // «загружена» у неё уже не вернуть.
          note={
            выгрузка.status === 'failed'
              ? 'Разобрать эту выгрузку второй раз нельзя. Примите её заново вместе с текстом — предложения появятся у новой выгрузки.'
              : undefined
          }
        />
        <FieldRow
          label='Разобрано сообщений'
          value={String(выгрузка.messages_total)}
          note='Столько сообщений модель прочитала. Предложением становится далеко не каждое.'
        />
        <FieldRow
          label='Предложений всего'
          value={String(выгрузка.drafts_total)}
          note={
            выгрузка.drafts_total === 0 && выгрузка.status === 'parsed'
              ? 'Разбор прошёл, но ни одного человека в переписке не опознано: смотрите протокол ниже.'
              : undefined
          }
        />
        <FieldRow
          label='Ждут решения'
          value={String(выгрузка.pending)}
          note={
            выгрузка.pending === 0 && выгрузка.drafts_total > 0
              ? 'По всем предложениям решения приняты.'
              : undefined
          }
        />
        <FieldRow
          label='Принята'
          value={принята === null ? null : formatDateDMY(принята)}
          missing='дата не записана'
        />
        <FieldRow
          label='Уничтожается'
          value={
            (выгрузка.erase_after ?? '').trim() === '' ? null : formatDateOnly(выгрузка.erase_after)
          }
          missing='срок не проставлен'
          note='Выгрузка — это переписка про живых людей, большинство которых в список так и не попадёт. После этой даты она стирается целиком вместе с неразобранными предложениями.'
        />
      </FieldList>

      <Block
        title='Протокол разбора'
        note='Что модель предложила, а проверка отбросила'
        action={
          можноРазбирать ? (
            // Кнопка не сереет без права и без текста: она остаётся нажимаемой и по нажатию
            // называет причину (правило 10). Выключенная кнопка не доводит нажатие до
            // обработчика, и человек не получает вообще ничего.
            <Button.Root
              type='button'
              variant='primary'
              mode='stroke'
              size='xsmall'
              onClick={разобрать}
            >
              <Button.Icon as={RiRobot2Line} />
              {mutation.isPending ? 'Разбираем…' : 'Разобрать выгрузку моделью'}
            </Button.Root>
          ) : undefined
        }
      >
        <div className='flex flex-col gap-2'>
          {gateNote !== null && <GateNote>{gateNote}</GateNote>}
          {mutation.isError && <ErrorAlert error={mutation.error} />}
          {(выгрузка.protocol ?? '').trim() === '' ? (
            // Три разных факта, и слипаться в одно «пусто» им нельзя (правило 7): протокола
            // ещё не было, протокол пуст потому, что отбрасывать было нечего, и протокола не
            // осталось после сбоя. Последнее — единственный случай, когда человеку надо
            // что-то сделать.
            <p className='text-paragraph-sm text-text-sub-600'>
              {можноРазбирать
                ? 'Протокола ещё нет: выгрузку не разбирали.'
                : выгрузка.status === 'failed'
                  ? 'Разбор не удался, и подробностей не осталось.'
                  : 'Разбор прошёл без замечаний: ни одно предложение не отброшено проверкой.'}
            </p>
          ) : (
            <p className='whitespace-pre-wrap text-paragraph-xs text-text-sub-600'>
              {выгрузка.protocol}
            </p>
          )}
        </div>
      </Block>
    </div>
  );
}

// ── Карточка предложения ────────────────────────────────────────────────────────────────

function КарточкаПредложения({ draftId, route }: { draftId: string; route: HiringRoute }) {
  const navigate = useNavigate();
  // Отдельного маршрута «одно предложение» в контракте нет, поэтому строка берётся из того же
  // ответа, что и список слева: параметры те же — значит и ключ кэша тот же, и второго запроса
  // к серверу не будет.
  const query = useDraftsQuery(draftsQueryFromRoute(route.search));
  const draft = query.data?.items.find((строка) => строка.id === draftId);

  return (
    <DetailPane
      title={
        <span className='flex min-w-0 items-center gap-2'>
          <RiSparkling2Line className='size-5 shrink-0 text-text-soft-400' />
          <span className='truncate'>
            {draft === undefined
              ? 'Предложение'
              : (draft.full_name ?? '').trim() === ''
                ? 'Имя не распознано'
                : draft.full_name}
          </span>
          {draft !== undefined && (
            <Badge.Root
              variant='lighter'
              color={DRAFT_VERDICT_COLORS[draft.verdict] ?? 'gray'}
              size='medium'
            >
              {draft.verdict_title}
            </Badge.Root>
          )}
        </span>
      }
      actions={
        draft !== undefined && draft.verdict === 'pending' ? (
          <Button.Root
            type='button'
            variant='primary'
            mode='stroke'
            size='xsmall'
            onClick={() => navigate(openPanelHref(route, PANEL_DRAFT_DECISION, draft.id))}
          >
            <Button.Icon as={RiScales3Line} />
            Принять решение
          </Button.Root>
        ) : undefined
      }
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='предложения из выгрузки мессенджера'
        isEmpty={() => draft === undefined}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-3 p-5'>
            <Skeleton className='h-24 w-full' />
            <Skeleton className='h-24 w-full' />
          </div>
        }
        empty={
          <div className='flex h-full items-center justify-center'>
            <EmptyState
              icon={RiErrorWarningLine}
              title='Этого предложения нет под текущим отбором'
              description='Ссылка ведёт на предложение из другой выгрузки или с другим решением. Снимите отбор — и оно вернётся в список.'
              action={
                <LinkButton.Root variant='primary' size='medium' underline asChild>
                  <Link to={hiringHref('drafts', { ...route.search, filters: {} }, draftId)}>
                    Снять отбор
                  </Link>
                </LinkButton.Root>
              }
            />
          </div>
        }
      >
        {() => (draft === undefined ? null : <ТелоПредложения draft={draft} />)}
      </ScreenState>
    </DetailPane>
  );
}

function ТелоПредложения({ draft }: { draft: BlacklistDraftOut }) {
  const решеноКогда = parseMoment(draft.decided_at);

  return (
    <div className='flex flex-col gap-3 p-5'>
      <p className='text-paragraph-sm text-text-sub-600'>
        Это предложение, а не запись. Автоматического пути из переписки в чёрный список нет: причину
        и правовое основание назначает человек, и до его решения этого человека сверка не находит.
      </p>

      <FieldList title='Что распознала модель'>
        <FieldRow label='Имя' value={непустое(draft.full_name)} missing='в сообщении не нашлось' />
        <FieldRow
          label='Телефон'
          value={непустое(draft.phone_normalized)}
          missing='в сообщении не нашлось'
        />
        <FieldRow
          label='Предложенная причина'
          value={непустое(draft.suggested_reason_title)}
          missing='модель причину не назвала'
          note='При решении её можно оставить или сменить: последнее слово за человеком.'
        />
        <FieldRow
          label='Замечание модели'
          value={непустое(draft.model_note)}
          missing='замечаний нет'
        />
      </FieldList>

      <FieldList title='Решение'>
        <FieldRow label='Решение' value={draft.verdict_title} />
        <FieldRow
          label='Когда принято'
          value={решеноКогда === null ? null : formatDateDMY(решеноКогда)}
          missing={draft.verdict === 'pending' ? 'решения ещё нет' : 'дата не записана'}
        />
        <FieldRow
          label='Кто принял'
          value={null}
          // Правило 7: неизвестное отличается от пустого и называет, кто ответит. Имени
          // автора решения в ответе нет, а показывать вместо него опознаватель — значит
          // показывать человеку служебное обозначение.
          missing={draft.verdict === 'pending' ? 'решения ещё нет' : 'видно в журнале действий'}
        />
        <FieldRow
          label='Заведённая запись'
          value={
            draft.entry_id === null || draft.entry_id === undefined ? null : (
              <LinkButton.Root variant='primary' size='small' underline asChild>
                <Link to={hiringHref('blacklist', { filters: {}, panels: [] }, draft.entry_id)}>
                  Открыть запись чёрного списка
                </Link>
              </LinkButton.Root>
            )
          }
          missing={
            draft.verdict === 'to_blacklist'
              ? 'запись не связана с предложением'
              : 'записи нет: решение было другим'
          }
        />
      </FieldList>

      <Block title='Исходное сообщение' note='Так оно выглядело в переписке'>
        {(draft.raw_text ?? '').trim() === '' ? (
          <p className='text-paragraph-sm text-text-sub-600'>
            Текст сообщения не сохранён: проверить, что именно распознала модель, не по чему.
          </p>
        ) : (
          <p className='whitespace-pre-wrap text-paragraph-sm text-text-strong-950'>
            {draft.raw_text}
          </p>
        )}
      </Block>
    </div>
  );
}

/** Пустая строка с сервера — это отсутствие значения, а не значение из пробелов. */
function непустое(значение: string | null | undefined): string | null {
  const текст = (значение ?? '').trim();
  return текст === '' ? null : текст;
}
